/**
 * Plain canvas drawing helpers for the image canvas viewport. They hold no
 * state and know nothing about Angular: every rect is in canvas CSS pixels and
 * `image` is the whole image in canvas pixels (see `image-canvas-labels.ts`).
 */
import {
    BOX_STROKE_WIDTH,
    DARK_TEXT_COLOR,
    heightLabelRect,
    LABEL_FONT,
    labelTextColor,
    TITLE_FONT,
    titleRect,
    widthLabelRect,
} from './image-canvas-labels';
import {
    HANDLE_SIZE,
    handlePositions,
    ImageRect,
} from './image-canvas-viewport';

export function strokeCanvasRect(
    context: CanvasRenderingContext2D,
    canvasRect: ImageRect,
    color: string,
    dashed: boolean
): void {
    context.save();
    context.strokeStyle = color;
    context.lineWidth = BOX_STROKE_WIDTH;
    context.setLineDash(dashed ? [6, 4] : []);
    context.strokeRect(
        canvasRect.x,
        canvasRect.y,
        canvasRect.width,
        canvasRect.height
    );
    context.restore();
}

/** Fills the label background and leaves a contrasting text color set. */
function fillLabel(
    context: CanvasRenderingContext2D,
    rect: ImageRect,
    color: string
): void {
    context.fillStyle = color;
    context.fillRect(rect.x, rect.y, rect.width, rect.height);
    context.fillStyle = labelTextColor(color);
}

export function drawTitle(
    context: CanvasRenderingContext2D,
    canvasRect: ImageRect,
    label: string,
    textWidth: number,
    color: string,
    below: boolean,
    image: ImageRect
): void {
    context.save();
    context.font = TITLE_FONT;
    const title = titleRect(canvasRect, textWidth, below, image);
    fillLabel(context, title, color);
    context.textBaseline = 'middle';
    context.fillText(label, title.x + 4, title.y + title.height / 2);
    context.restore();
}

export function drawSizeLabels(
    context: CanvasRenderingContext2D,
    canvasRect: ImageRect,
    rect: ImageRect,
    color: string,
    image: ImageRect
): void {
    const widthText = `${Math.round(rect.width)} px`;
    const heightText = `${Math.round(rect.height)} px`;
    context.save();
    context.beginPath();
    context.rect(image.x, image.y, image.width, image.height);
    context.clip();
    context.font = LABEL_FONT;
    context.textBaseline = 'middle';
    context.textAlign = 'center';

    const widthLabel = widthLabelRect(
        canvasRect,
        context.measureText(widthText).width,
        image
    );
    fillLabel(context, widthLabel, color);
    context.fillText(
        widthText,
        widthLabel.x + widthLabel.width / 2,
        widthLabel.y + widthLabel.height / 2
    );

    const heightLabel = heightLabelRect(
        canvasRect,
        context.measureText(heightText).width,
        image
    );
    fillLabel(context, heightLabel, color);
    context.translate(
        heightLabel.x + heightLabel.width / 2,
        heightLabel.y + heightLabel.height / 2
    );
    context.rotate(-Math.PI / 2);
    context.fillText(heightText, 0, 0);
    context.restore();
}

export function drawHandles(
    context: CanvasRenderingContext2D,
    canvasRect: ImageRect,
    color: string
): void {
    const half = HANDLE_SIZE / 2;
    context.save();
    context.fillStyle = color;
    context.strokeStyle = DARK_TEXT_COLOR;
    context.lineWidth = 1;
    for (const { x, y } of handlePositions(canvasRect)) {
        context.fillRect(x - half, y - half, HANDLE_SIZE, HANDLE_SIZE);
        context.strokeRect(x - half, y - half, HANDLE_SIZE, HANDLE_SIZE);
    }
    context.restore();
}
